import _ from 'lodash';
import submenuModels from './menus/submenu-models';
import CRUDPage from './CRUDPage';
import NiceDate from '../components/common/NiceDate';
import ModelContextEditor from '../components/lego/ModelContextEditor';
import MongoDocEditor from '../components/lego/MongoDocEditor';
import TrimSpec from '../components/lego/trims/TrimSpec';
import { LabelsSummary } from '../components/data-issues/LabelsSummary';
import BadgeId from '../components/common/BadgeId';
import { IconButton } from '../components/common/IconButton';
import ModalBatchEditDoc, { defaultColumns } from '../components/lego/ModalBatchEditDoc';
import { MiniatureStateSwitch, STATE_COLORS } from '../components/miniatures/miniatureUiUtils';
import ScaledMiniature, { computeMiniaturesScale } from '../components/miniatures/ScaledMiniature';

function ModelMiniaturesFiltersBar({ filter, onChange }) {
  const onFieldChange = (field, value) => {
    let newFilter = _.cloneDeep(filter || {});

    // Match GenerationsManager: a modelId selection filters that exact id, a make-only selection
    // becomes a prefix regex. Applied to the array element key `context.modelId`.
    if (field === 'context.modelId') {
      if (value.modelId) {
        value = value.modelId;
      } else if (value.make) {
        value = { $regex: '^' + value.make };
      }
    }

    if (_.isEmpty(value)) {
      delete newFilter[field];
    } else {
      newFilter[field] = _.cloneDeep(value);
    }

    onChange(newFilter);
  };

  const modelIdFilter = (filter || {})['context.modelId'];
  let context = null;
  if (modelIdFilter?.$regex) {
    context = { make: modelIdFilter.$regex.slice(1) };
  } else if (modelIdFilter) {
    context = { modelId: modelIdFilter };
  }

  const stateValue = (filter || {}).state;
  const stateArr = _.isArray(stateValue) ? stateValue : (stateValue ? [stateValue] : []);

  return <div className={'d-flex align-items-center'}>
    <span style={{ width: '350px', display: 'inline-block' }} className={'zoom-75 ml-2 mr-2'}>
      <ModelContextEditor value={context}
                          onChange={(newContext) => onFieldChange('context.modelId', newContext || {})}
                          groupsToUse={['makes', 'models', 'families', 'submodels']} />
    </span>

    <span className={'zoom-90 mr-3 align-middle'}>
      <MiniatureStateSwitch multi value={stateArr} onChange={(states) => onFieldChange('state', states)} />
    </span>
  </div>;
}

export default class ModelMiniaturesManager extends CRUDPage {
  constructor(props) {
    super(props, '/services/data/model-miniatures');

    this.submenu = submenuModels;
    this.state.columnSort = { index: 0, asc: false, sorter: 'updatedAt' };
    this.newObjectTemplate = {
      context: [],
      state: 'inprogress',
    };
  }

  getFacetFiltersComponent() {
    return <ModelMiniaturesFiltersBar onChange={this.onFiltersChange.bind(this)} filter={this.state.facetFilters} />;
  }

  openBatchEditContexts(docs) {
    const columns = [defaultColumns.context];
    this.openModal(
      <ModalBatchEditDoc docs={docs} onCancel={() => this.closeModal()} onSave={() => true} service={this.views} columns={columns} />,
      { title: 'Edit miniatures context in batch' }
    );
  }

  getSelectionButtons(selection) {
    if (selection?.length) {
      return <>
        {super.getSelectionButtons(selection)}
        <IconButton icon={'app_registration'} title={'Edit context in batch'} onClick={() => this.openBatchEditContexts(selection)} />
      </>;
    }
    return super.getSelectionButtons(selection);
  }

  getColumnsDefinition() {
    return [
      { content: 'Last update', className: 'text-center', sorter: 'updatedAt' },
      { content: 'Image', className: 'text-center' },
      { content: 'Context', className: 'text-left', sorter: 'context.0.modelId' },
      { content: 'State', className: 'text-center', sorter: 'state' },
      { content: 'Dimensions (mm)', className: 'text-center' },
    ];
  }

  getRowClasses(obj, i, rows) {
    const prevModelId = rows?.[i - 1]?.context?.[0]?.modelId;
    const modelId = obj?.context?.[0]?.modelId;
    return prevModelId !== modelId ? 'row-border-top-3' : '';
  }

  // Shared physical scale across the visible rows so miniatures are size-comparable. Memoized per
  // rows array (CRUDPage passes a stable slice within a render pass).
  miniaturesScale(rows) {
    if (this._scaleRows !== rows) {
      this._scaleRows = rows;
      this._scaleVal = computeMiniaturesScale(rows, { maxHeight: 120, maxWidth: 240 });
    }
    return this._scaleVal;
  }

  getObjectColumns(miniature, def, i, allRows) {
    const { context, state, image, dimensions, overlayBox, labels, updatedAt } = miniature;

    return [
      <td key={'upd'}>
        {updatedAt && <NiceDate t={updatedAt} />}
        {labels?.length ? <><br /><LabelsSummary labels={labels} /></> : null}
        <div className={'mt-1'}><BadgeId id={miniature._id} /></div>
      </td>,

      <td key={'img'} className={'text-center'}>
        {image
          ? <div className={'d-inline-block'}><ScaledMiniature miniature={miniature} crop pxPerMm={this.miniaturesScale(allRows)} /></div>
          : <span className={'text-muted small'}>no image</span>}
      </td>,

      <td key={'ctx'} className={'text-left'}>
        {(context || []).map((c, idx) => <div key={idx} className={'no-wrap'}>
          <TrimSpec spec={'modelId'} className={'mr-2'} value={c.modelId} />
          {c.genId ? <TrimSpec spec={'genId'} value={c.genId} /> : null}
          {c.year ? <span className={'badge badge-light ml-1'}>{c.year}</span> : null}
        </div>)}
      </td>,

      <td key={'state'} className={'text-center'}>
        <span title={state} className={`badge badge-${STATE_COLORS[state] || 'dark'}`}>{state || '-'}</span>
      </td>,

      <td key={'dims'} className={'text-center text-info small no-wrap'}>
        {dimensions ? <span>{dimensions.widthMm} W × {dimensions.heightMm} H × {dimensions.lengthMm} L</span> : null}
        {overlayBox?.view ? <div className={'text-muted'}>{overlayBox.view}</div> : null}
      </td>,
    ];
  }

  getObjectEditorComponent(obj, defaultOnSaveCbk, defaultOnCancelCbk) {
    // `context` uses MongoDocEditor's built-in MultipleContextsEditor; `state` gets our switch
    // (LegoStateSwitch lacks review/failed). Everything else uses the default JSON GUI editors.
    const schema = {
      state: (value, onChange) => <MiniatureStateSwitch value={value} onChange={onChange} />,
    };
    return <MongoDocEditor extendSchema={schema} hideState obj={obj} onSave={defaultOnSaveCbk} onCancel={defaultOnCancelCbk} />;
  }
}
